import { useCallback, useEffect, useRef, useState } from 'react';

import { match, P } from 'ts-pattern';

import { useMicStore, useRecordingControls, useRecordingStore } from '@/entities/audio-recording';

export type AutoStartPhase = 'waiting-for-mic' | 'blocked' | 'failed' | 'started';

export function useAutoStartDictation() {
    const accessDenied = useMicStore((state) => state.accessDenied);
    const micStream = useMicStore((state) => state.currentStream);
    const status = useRecordingStore((state) => state.status);
    const hasStartFailed = useRecordingStore((state) => state.hasStartFailed);
    const clearStartFailure = useRecordingStore((state) => state._clearStartFailure);
    const { start } = useRecordingControls();

    const [hasFailed, setHasFailed] = useState(false);
    const hasAttemptedRef = useRef(false);

    const attemptStart = useCallback(async () => {
        setHasFailed(!(await start(crypto.randomUUID(), false)));
    }, [start]);

    useEffect(() => {
        if (
            hasAttemptedRef.current ||
            hasStartFailed ||
            accessDenied !== false ||
            !micStream?.active
        ) {
            return;
        }

        // Claimed before the await so a StrictMode double-invoke, a re-render or a mic swap can't
        // queue a second recording. Never released — only an explicit retry starts another.
        // hasStartFailed covers the remount a rejected start causes, which loses this ref.
        hasAttemptedRef.current = true;
        void attemptStart();
    }, [accessDenied, micStream, hasStartFailed, attemptStart]);

    const retry = () => {
        // Clearing the failure re-runs the effect, which would otherwise auto-start a second time.
        hasAttemptedRef.current = true;
        clearStartFailure();
        setHasFailed(false);
        void attemptStart();
    };

    const phase = match({ status, accessDenied, hasFailed, hasStartFailed })
        .returnType<AutoStartPhase>()
        .with({ status: P.not('idle') }, () => 'started')
        .with({ accessDenied: true }, () => 'blocked')
        .with(P.union({ hasFailed: true }, { hasStartFailed: true }), () => 'failed')
        .otherwise(() => 'waiting-for-mic');

    return { phase, retry };
}
